import type {
  Audience,
  FieldCondition,
  FieldConditionOp,
} from '@fundline/shared/schemas/campaigns';
import { FIELD_CONDITION_LABELS } from '@fundline/shared/schemas/campaigns';

/**
 * The audience builder's form rules (M9), pure so they are unit-tested. The worker checks every
 * condition again against the workspace's fields before anything is matched.
 */

export const US_STATES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]);

/** "ny, nj  FL" → ['NY','NJ','FL'], or the first code that isn't a US state. */
export function parseStates(
  text: string,
): { ok: true; states: string[] } | { ok: false; error: string } {
  const codes = text
    .split(/[\s,;]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s !== '');
  for (const code of codes) {
    if (!US_STATES.has(code)) return { ok: false, error: `"${code}" isn't a US state code` };
  }
  return { ok: true, states: [...new Set(codes)] };
}

export const needsValue = (op: FieldConditionOp) => op !== 'empty' && op !== 'not_empty';

/** The stored value of a condition from what the admin typed, by field type. */
export function conditionValue(
  type: string,
  op: FieldConditionOp,
  raw: string,
): { ok: true; value: FieldCondition['value'] } | { ok: false; error: string } {
  if (!needsValue(op)) return { ok: true, value: null };
  const text = raw.trim();
  if (text === '') return { ok: false, error: 'Enter a value' };
  switch (type) {
    case 'number': {
      const n = Number(text.replaceAll(',', ''));
      return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: 'Enter a number' };
    }
    case 'currency': {
      const m = /^\$?\s*(\d{1,14})(?:\.(\d{1,2}))?$/.exec(text.replaceAll(',', ''));
      if (!m) return { ok: false, error: 'Enter an amount in dollars' };
      return { ok: true, value: Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0')) };
    }
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(text)
        ? { ok: true, value: text }
        : { ok: false, error: 'Pick a date' };
    case 'boolean':
      if (/^(yes|true)$/i.test(text)) return { ok: true, value: true };
      if (/^(no|false)$/i.test(text)) return { ok: true, value: false };
      return { ok: false, error: 'Choose yes or no' };
    default:
      return { ok: true, value: text.slice(0, 200) };
  }
}

/** What the admin typed, back from a stored value (to edit a saved condition). */
export function conditionInput(type: string, value: FieldCondition['value']): string {
  if (value === null) return '';
  if (type === 'currency' && typeof value === 'number') {
    return value % 100 === 0 ? String(value / 100) : (value / 100).toFixed(2);
  }
  if (type === 'boolean') return value === true ? 'yes' : 'no';
  return String(value);
}

export interface AudienceNames {
  tags: ReadonlyMap<string, string>;
  imports: ReadonlyMap<string, string>;
  reps: ReadonlyMap<string, string>;
  fields: ReadonlyMap<string, { label: string; type: string }>;
}

function list(ids: readonly string[], names: ReadonlyMap<string, string>): string {
  return ids.map((id) => names.get(id) ?? 'a removed item').join(', ');
}

/** One line per filter, in plain words, for the review and the campaign screen. */
export function describeAudience(a: Audience, n: AudienceNames): string[] {
  const lines: string[] = [];
  if (a.everyone) lines.push('Everyone in your workspace');
  if (a.tagsAny.length)
    lines.push(`Tagged ${a.tagsAny.length > 1 ? 'any of ' : ''}${list(a.tagsAny, n.tags)}`);
  if (a.tagsAll.length) lines.push(`Tagged all of ${list(a.tagsAll, n.tags)}`);
  if (a.sources.length) lines.push(`Source: ${a.sources.join(', ')}`);
  if (a.importIds.length) lines.push(`From the import ${list(a.importIds, n.imports)}`);
  if (a.repIds.length) lines.push(`Assigned to ${list(a.repIds, n.reps)}`);
  if (a.states.length) lines.push(`In ${a.states.join(', ')}`);
  for (const c of a.fields) {
    const f = n.fields.get(c.fieldId);
    const label = f?.label ?? 'A removed field';
    const value = needsValue(c.op) ? ` ${conditionInput(f?.type ?? 'text', c.value)}` : '';
    lines.push(`${label} ${FIELD_CONDITION_LABELS[c.op]}${value}`);
  }
  if (a.excludeTags.length) lines.push(`Not tagged ${list(a.excludeTags, n.tags)}`);
  return lines;
}
